import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../../components/ui/dialog";
import { Calendar } from "../../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import { CalendarIcon, Clock, Plus } from "lucide-react";
import { format } from "date-fns";

const FOLLOW_UP_TYPES = [
  { value: "lead_feedback", label: "Lead feedback" },
  { value: "sample_followup", label: "Sample follow-up" },
  { value: "order_followup", label: "Order follow-up" },
  { value: "payment_followup", label: "Payment follow-up" },
  { value: "others", label: "Others" },
];

const getOutcomeLabel = (outcome) => {
  const found = FOLLOW_UP_TYPES.find(t => t.value === outcome);
  return found ? found.label : outcome;
};

const FollowupsSection = ({
  followups,
  isAdmin,
  showFollowupDialog,
  setShowFollowupDialog,
  newFollowup,
  setNewFollowup,
  selectedDate,
  setSelectedDate,
  selectedTime,
  setSelectedTime,
  handleCreateFollowup,
  handleCompleteFollowup,
  getPriorityColor
}) => {
  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-4 flex flex-row items-center justify-between">
        <CardTitle className="text-lg">Follow-ups</CardTitle>
        <Dialog open={showFollowupDialog} onOpenChange={setShowFollowupDialog}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" data-testid="add-followup-btn">
                <Plus className="w-4 h-4 mr-1" />Add
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Schedule Follow-up</DialogTitle>
                <DialogDescription>Create a reminder to follow up with this lead</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateFollowup} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Follow-up Type *</Label>
                  <Select value={newFollowup.outcome || ""} onValueChange={(value) => setNewFollowup({ ...newFollowup, outcome: value, outcome_notes: value !== "others" ? "" : newFollowup.outcome_notes })}>
                    <SelectTrigger data-testid="followup-outcome-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>
                      {FOLLOW_UP_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {newFollowup.outcome === "others" && (
                  <div className="space-y-2">
                    <Label>Comments (used as title for "Others") *</Label>
                    <textarea 
                      value={newFollowup.outcome_notes || ""} 
                      onChange={(e) => setNewFollowup({ ...newFollowup, outcome_notes: e.target.value })} 
                      rows={2} 
                      required
                      placeholder="Please describe the follow-up reason..."
                      className="flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      data-testid="followup-outcome-notes-input" 
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Description</Label>
                  <textarea 
                    value={newFollowup.description} 
                    onChange={(e) => setNewFollowup({ ...newFollowup, description: e.target.value })} 
                    rows={2} 
                    className="flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    data-testid="followup-desc-input" 
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start" data-testid="followup-date-btn">
                          <CalendarIcon className="w-4 h-4 mr-2" />{format(selectedDate, "PP")}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar mode="single" selected={selectedDate} onSelect={(date) => date && setSelectedDate(date)} initialFocus />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label>Time</Label>
                    <Input 
                      type="time" 
                      value={selectedTime} 
                      onChange={(e) => setSelectedTime(e.target.value)}
                      className="h-10"
                      data-testid="followup-time-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Priority</Label>
                    <Select value={newFollowup.priority} onValueChange={(value) => setNewFollowup({ ...newFollowup, priority: value })}>
                      <SelectTrigger data-testid="followup-priority-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>LFB ID</Label>
                    <Input 
                      value={newFollowup.lfb_id || ""} 
                      onChange={(e) => setNewFollowup({ ...newFollowup, lfb_id: e.target.value })} 
                      placeholder="e.g., LFB-001 (optional)" 
                      data-testid="followup-lfb-input" 
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="submit-followup-btn">Schedule</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
      </CardHeader>
      <CardContent>
        {followups.length > 0 ? (
          <div className="space-y-3">
            {followups.map((followup) => {
              const scheduledDate = new Date(followup.scheduled_at);
              const isPastDue = !followup.completed && scheduledDate < new Date();
              const displayTitle = followup.outcome === 'others' 
                ? (followup.outcome_notes || followup.title || 'Others') 
                : (getOutcomeLabel(followup.outcome) || followup.title || 'Follow-up');
              return (
                <div 
                  key={followup.id} 
                  className={`p-3 rounded-md border ${followup.completed ? 'bg-slate-50 opacity-60' : isPastDue ? 'bg-red-50 border-red-200' : 'bg-white'}`} 
                  data-testid={`followup-${followup.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <p className={`font-medium text-sm ${followup.completed ? 'line-through text-slate-400' : 'text-slate-900'}`}>
                        {displayTitle}
                      </p>
                      {followup.description && (
                        <p className="text-xs text-slate-500 mt-1">{followup.description}</p>
                      )}
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <Clock className={`w-3 h-3 ${isPastDue ? 'text-red-500' : 'text-slate-400'}`} />
                        <span className={`text-xs ${isPastDue ? 'text-red-600 font-medium' : 'text-slate-500'}`}>
                          {scheduledDate.toLocaleDateString()} at {scheduledDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        {isPastDue && <span className="text-xs text-red-600 font-medium">(Overdue)</span>}
                        <span className={`${getPriorityColor(followup.priority)} px-1.5 py-0.5 rounded text-xs capitalize`}>
                          {followup.priority}
                        </span>
                        {followup.outcome === 'others' && followup.outcome_notes && followup.description && (
                          <span className="text-xs text-slate-500 italic">- {followup.outcome_notes}</span>
                        )}
                        {followup.lfb_id && (
                          <span className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded text-xs font-mono">
                            {followup.lfb_id}
                          </span>
                        )}
                      </div>
                    </div>
                    {!followup.completed && (
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => handleCompleteFollowup(followup.id)} 
                        className="h-7 text-xs" 
                        data-testid={`complete-followup-${followup.id}`}
                      >
                        Done
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-center py-6 text-slate-400 text-sm">No follow-ups scheduled</p>
        )}
      </CardContent>
    </Card>
  );
};

export default FollowupsSection;
